/**
 * API hry Unmatched — typy stavu a volania zápasov. Spoločný HTTP klient
 * a chyby sú v portálovom `api.ts`; `ApiError` sa tu re-exportuje, aby
 * obrazovky hry mali všetko z jedného miesta.
 */

import type { Action, GameEvent, HeroDef, PendingChoice, PlayerIndex } from '@unmatched/engine';

import { api } from '../../api.js';

export { ApiError } from '../../api.js';

export interface PublicPlayer {
  index: PlayerIndex;
  heroId: string;
  team: number;
  eliminated: boolean;
  hand: (string | null)[];
  handSize: number;
  deckSize: number;
  discard: string[];
  /** Trvalá schéma (ONGOING), ktorá leží pred hráčom. */
  ongoing?: string | null;
  /** Premenlivá podoba hrdinu (Alice: `big` / `small`). */
  form?: string | null;
  /** Verejné počítadlá (Beowulf: `rage`). */
  counters?: Record<string, number>;
  /** Vibranium Suit (Black Panther) — súperove karty lícom hore, len na boost. */
  suit?: string[];
}

export interface PublicState {
  version: number;
  mapId: string;
  sandbox: boolean;
  fighters: {
    id: string;
    owner: PlayerIndex;
    kind: 'hero' | 'sidekick';
    defId: string;
    name: string;
    hp: number;
    maxHp: number;
    move: number;
    attack: 'melee' | 'ranged';
    spaceId: string;
  }[];
  cards: Record<string, { id: string; defId: string; owner: PlayerIndex }>;
  players: PublicPlayer[];
  turn: {
    player: PlayerIndex;
    actionsUsed: ('maneuver' | 'scheme' | 'attack')[];
    number: number;
    /** Akcie navyše (+) alebo prepadnuté (−) v tomto ťahu. */
    extraActions?: number;
  };
  /** Žetóny na doske (hmla Invisible Mana). */
  tokens?: { id: string; kind: 'fog'; owner: PlayerIndex; spaceId: string }[];
  /** Figúrky dočasne mimo dosky (Vanish). */
  limbo?: PublicState['fighters'];
  /** Porazené figúrky (a klony Sun Wukonga v zálohe — vyvolanie ich vracia na dosku). */
  defeated?: PublicState['fighters'];
  maneuver: {
    player: PlayerIndex;
    movedFighters: string[];
    boostUsed: boolean;
    pendingBoost: number;
  } | null;
  /** Rozhodnutia, ktoré si vypýtali efekty kariet; aktívne je prvé. */
  pending: PendingChoice[];
  /** Súčasné tajné odhalenie (Lodge of Sorceresses); cudzie voľby sú `'hidden'`. */
  reveal?: { source: string; player: PlayerIndex; picks: Record<number, string | null> } | null;
  combat: {
    attackerFighter: string;
    defenderFighter: string;
    attackerPlayer: PlayerIndex;
    defenderPlayer: PlayerIndex;
    attackCard: string | null;
    defenseCard: string | null;
    defenderPassed: boolean;
    step: string;
    /** Kde súboj čaká na rozhodnutie uprostred vyhodnotenia. */
    stage?: 'immediate' | 'immediateAttacker' | 'during' | 'values';
    /** Útočná karta zahraná lícom hore (Foul Purpose). */
    faceUp?: boolean;
    /** Bonusový útok (Relentless Assault): meno a hodnota namiesto karty. */
    bonus?: { name: string; value: number };
    /** Spidey-Sense: vytlačená hodnota útoku ohlásená pred obranou. */
    announced?: number;
  } | null;
  phase: 'setup' | 'turn' | 'combat' | 'ended';
  format: GameFormat;
  /** Víťazný TÍM (v duele a FFA je to číslo hráča). */
  winner: number | null;
  seq: number;
  viewer: PlayerIndex;
}

export type MatchStatus = 'lobby' | 'playing' | 'ended' | 'abandoned';
export type MatchMode = 'hotseat' | 'online' | 'solo';
export type BotLevel = 'easy' | 'medium' | 'hard';

export const BOT_LEVEL_LABEL: Record<BotLevel, string> = {
  easy: 'Ľahký',
  medium: 'Stredný',
  hard: 'Ťažký',
};
export type GameFormat = 'duel' | 'ffa' | 'teams';

export interface MatchView {
  id: string;
  status: MatchStatus;
  mode: MatchMode;
  /** Verejný stôl — v zozname ho vidí každý a pridať sa smie ktokoľvek. */
  isPublic: boolean;
  format: GameFormat;
  seatsTotal: number;
  seatsFree: number;
  mapId: string;
  seats: {
    slot: number;
    heroId: string | null;
    nick: string | null;
    isBot: boolean;
    botLevel: BotLevel | null;
    isYou: boolean;
    team: number;
    eliminated: boolean;
  }[];
  yourSlots: PlayerIndex[];
  viewer: PlayerIndex;
  /** V lobby (čaká sa na súpera) stav hry ešte neexistuje. */
  state: PublicState | null;
  legal: Action[];
  canJoin: boolean;
  activeSlot: PlayerIndex | null;
  watchers?: number[];
  /** Miesta, ktorých hráč má stôl otvorený (boti vždy) — posiela server. */
  online?: number[];
}

export interface MatchSummary {
  id: string;
  map_id: string;
  status: MatchStatus;
  mode: MatchMode;
  format: GameFormat;
  seats_total: number;
  seats_free: number;
  winner: number | null;
  heroes: (string | null)[];
  nicks: (string | null)[];
  /** Úroveň bota pri každom mieste; `null` = človek. */
  bot_levels: (BotLevel | null)[];
  actions: number;
  is_public: boolean;
  /** Číslo ťahu, v lobby `null`. */
  turn: number | null;
  /** Čaká hra práve na mňa (môj ťah, obrana alebo rozhodnutie)? */
  your_move: boolean;
  created_at: string;
  updated_at: string;
}

/** Riadok zoznamu všetkých zápasov. `id` len pri mojich a verejných stoloch v predsieni — odkaz s ID je pozvánka. */
export interface PublicMatch {
  id: string | null;
  key: string;
  mine: boolean;
  is_public: boolean;
  map_id: string;
  status: MatchStatus;
  mode: MatchMode;
  format: GameFormat;
  seats_total: number;
  winner: number | null;
  created_at: string;
  updated_at: string;
  ended_at: string | null;
  heroes: (string | null)[];
  nicks: (string | null)[];
  bot_levels: (BotLevel | null)[];
  turn: number | null;
}

/** Verejný stôl čakajúci na hráčov — pridať sa smie ktokoľvek. */
export interface PublicTable {
  id: string;
  map_id: string;
  format: GameFormat;
  seats_total: number;
  seats_free: number;
  heroes: (string | null)[];
  nicks: (string | null)[];
  host: string | null;
  mine: boolean;
  created_at: string;
}

export const matches = {
  list: () => api.get<{ matches: MatchSummary[] }>('/matches'),
  all: (filter: { mode?: MatchMode; status?: MatchStatus; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filter)) if (value !== undefined) params.set(key, String(value));
    return api.get<{ matches: PublicMatch[]; total: number }>(`/matches/all?${params}`);
  },
  publicTables: () => api.get<{ tables: PublicTable[] }>('/matches/public'),
  options: () =>
    api.get<{ maps: { id: string; name: string; spaces: number }[]; heroes: HeroDef[] }>(
      '/matches/meta/options',
    ),
  create: (opts: {
    mapId: string;
    heroes: string[];
    mode: MatchMode;
    players: number;
    format: GameFormat;
    botLevel?: BotLevel;
    /** Online: verejný stôl. */
    public?: boolean;
  }) => api.post<MatchView>('/matches', opts),
  join: (id: string, heroId: string) => api.post<MatchView>(`/matches/${id}/join`, { heroId }),
  get: (id: string) => api.get<MatchView>(`/matches/${id}`),
  act: (id: string, action: Action) =>
    api.post<{ view: MatchView; events: GameEvent[] }>(`/matches/${id}/actions`, { action }),
  /** Priebeh zápasu zo servera — bez neho by po refreshi zostal log prázdny. */
  log: (id: string) => api.get<{ log: MatchLogRow[] }>(`/matches/${id}/log`),
};

export interface MatchLogRow {
  seq: number;
  events: GameEvent[];
  by_user_id: number | null;
  created_at: string;
}
